import { useEffect, useState } from 'react'

import type { DialogProps } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'

import { StepConfirmation } from 'app/KinerjaShared/components/PltKepalaSekolahConfirmationDialog/StepConfirmation'
import { StepSelectSchool } from 'app/KinerjaShared/components/PltKepalaSekolahConfirmationDialog/StepSelectSchool'
import { StepSuccess } from 'app/KinerjaShared/components/PltKepalaSekolahConfirmationDialog/StepSuccess'

import type { PelaksanaanSchool } from 'types/kinerja-pelaksanaan'

type PltKepalaSekolahConfirmationDialogProps = Omit<DialogProps, 'children'> & {
  schools: Array<PelaksanaanSchool>
  kepalaSekolahLabel?: string
  pegawaiLabel?: string
}

type Step = 'SELECT_SCHOOL' | 'CONFIRMATION' | 'SUCCESS'

export function PltKepalaSekolahConfirmationDialog({
  isOpen,
  schools: schoolsProp,
  kepalaSekolahLabel = 'Kepala Sekolah',
  pegawaiLabel = 'Guru',
  onClose,
  ...props
}: PltKepalaSekolahConfirmationDialogProps) {
  // -----------------------------------------
  // Handle step
  // -----------------------------------------
  const [activeStep, setActiveStep] = useState<Step>('SELECT_SCHOOL')
  // Reset step upon mount
  useEffect(() => {
    setActiveStep('SELECT_SCHOOL')
  }, [isOpen])

  // -----------------------------------------
  // Handle schools selection
  // -----------------------------------------
  const [schools, setSchools] = useState<Array<PelaksanaanSchool>>([])

  // Sync schools data with local state
  useEffect(() => {
    setSchools(resolvePltKepalaSekolahConfirmationSchools(schoolsProp))
  }, [schoolsProp])

  return (
    <Dialog
      size="sm"
      isOpen={isOpen}
      closeOnOverlayClick={false}
      onClose={onClose}
      {...props}
    >
      <DialogOverlay />
      <DialogContent className="p-6">
        {activeStep === 'SELECT_SCHOOL' && (
          <StepSelectSchool
            schools={schools}
            kepalaSekolahLabel={kepalaSekolahLabel}
            pegawaiLabel={pegawaiLabel}
            onChangeSelectedSchools={(schools) => setSchools(schools)}
            onNext={() => setActiveStep('CONFIRMATION')}
            onPrev={() => onClose?.()}
          />
        )}

        {activeStep === 'CONFIRMATION' && (
          <StepConfirmation
            schools={schools}
            onClose={() => onClose?.()}
            onPrev={() => setActiveStep('SELECT_SCHOOL')}
            onSuccess={() => setActiveStep('SUCCESS')}
          />
        )}

        {activeStep === 'SUCCESS' && (
          <StepSuccess onClose={() => onClose?.()} />
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * Resolve the school list shown in the Plt. Kepala Sekolah confirmation dialog.
 * Returns shallow copies with only the fields used in the dialog flow
 * to avoid mutation on the original objects.
 */
export function resolvePltKepalaSekolahConfirmationSchools(
  schools: PelaksanaanSchool[]
): PelaksanaanSchool[] {
  // Only include the schools if:
  // - role is Plt, AND
  // - school is negeri, AND
  // - status is either unconfirmed or rejected
  return (
    schools
      .filter(
        (school) =>
          (school.role === 'KS_PLT_SPNF' || school.role === 'KS_PLT') &&
          school.statusSatuanPendidikan === 'NEGERI' &&
          (school.confirmationStatus === 'UNCONFIRMED' ||
            school.confirmationStatus === 'REJECTED')
      )
      // Create shallow copy with .map() to avoid mutation on the original object
      // that might be used somewhere else via react-query cache
      .map(
        ({
          schoolName,
          npsn,
          role,
          confirmationStatus,
          perencanaanReadyState,
          statusSatuanPendidikan,
        }) => ({
          schoolName,
          npsn,
          role,
          confirmationStatus,
          perencanaanReadyState,
          statusSatuanPendidikan,
        })
      )
  )
}
